
import { universalStorage } from '../storage/universalStorage';

export interface AppSettings {
  theme: 'light' | 'dark' | 'system';
  language: string;
  timezone: string;
  dateFormat: string;
  currency: string;
  notifications: {
    enabled: boolean;
    sound: boolean;
    email: boolean;
    desktop: boolean;
  };
  backup: {
    autoBackup: boolean;
    backupFrequency: 'daily' | 'weekly' | 'monthly';
    maxBackups: number;
  };
  security: {
    sessionTimeout: number;
    requirePassword: boolean;
    passwordHash?: string;
  };
  display: {
    itemsPerPage: number;
    showImages: boolean;
    compactView: boolean;
  };
}

export class UniversalSettingsService {
  private static instance: UniversalSettingsService;
  private settingsCache: Map<string, any> = new Map();

  static getInstance(): UniversalSettingsService {
    if (!UniversalSettingsService.instance) {
      UniversalSettingsService.instance = new UniversalSettingsService();
    }
    return UniversalSettingsService.instance;
  }

  async getSettings(): Promise<AppSettings> {
    try {
      console.log('⚙️ Loading settings from localStorage only (5000+ capacity)...');
      
      // Always get from localStorage immediately
      const settings = await universalStorage.getItem<AppSettings>('nalc_app_settings');
      const result = settings || this.getDefaultSettings();
      
      console.log('✅ Settings loaded successfully from localStorage');
      return result;
    } catch (error) {
      console.error('Error loading settings:', error);
      return this.getDefaultSettings();
    }
  }

  async updateSettings(settings: Partial<AppSettings>): Promise<void> {
    try {
      console.log('⚙️ Updating settings in localStorage with 5000+ capacity...');
      
      const currentSettings = await this.getSettings();
      const updatedSettings = { ...currentSettings, ...settings };
      
      // Update localStorage immediately with enhanced persistence
      await universalStorage.setItem('nalc_app_settings', updatedSettings);
      this.settingsCache.set('app_settings', updatedSettings);
      
      console.log('✅ Settings updated successfully in localStorage');
    } catch (error) {
      console.error('Error updating settings:', error);
      throw error;
    }
  }

  async getSetting<T>(key: string): Promise<T | null> {
    try {
      if (this.settingsCache.has(key)) {
        return this.settingsCache.get(key);
      }
      
      // Always get from localStorage immediately
      const value = await universalStorage.getItem<T>(`nalc_setting_${key}`);
      if (value !== null) {
        this.settingsCache.set(key, value);
      }
      
      return value;
    } catch (error) {
      console.error(`Error getting setting ${key}:`, error);
      return null;
    }
  }

  async setSetting<T>(key: string, value: T): Promise<void> {
    try {
      console.log(`⚙️ Setting ${key} in localStorage with enhanced persistence...`);
      
      // Update localStorage immediately
      await universalStorage.setItem(`nalc_setting_${key}`, value);
      this.settingsCache.set(key, value);
      
      console.log(`✅ Setting ${key} updated successfully`);
    } catch (error) {
      console.error(`Error setting ${key}:`, error);
      throw error;
    }
  }

  async getNotificationSettings(): Promise<AppSettings['notifications']> {
    try {
      // Always get from localStorage immediately
      const settings = await universalStorage.getItem<AppSettings['notifications']>('nalc_notification_settings');
      const result = settings || {
        enabled: true,
        sound: true,
        email: false,
        desktop: true
      };
      
      return result;
    } catch (error) {
      console.error('Error loading notification settings:', error);
      return {
        enabled: true,
        sound: true,
        email: false,
        desktop: true
      };
    }
  }

  async updateNotificationSettings(settings: Partial<AppSettings['notifications']>): Promise<void> {
    try {
      console.log('⚙️ Updating notification settings in localStorage...');
      
      const currentSettings = await this.getNotificationSettings();
      const updatedSettings = { ...currentSettings, ...settings };
      
      // Update localStorage immediately
      await universalStorage.setItem('nalc_notification_settings', updatedSettings);
      
      console.log('✅ Notification settings updated successfully');
    } catch (error) {
      console.error('Error updating notification settings:', error);
      throw error;
    }
  }

  async getBackupSettings(): Promise<AppSettings['backup']> {
    try {
      // Always get from localStorage immediately
      const settings = await universalStorage.getItem<AppSettings['backup']>('nalc_backup_settings');
      return settings || {
        autoBackup: false,
        backupFrequency: 'weekly',
        maxBackups: 10
      };
    } catch (error) {
      console.error('Error loading backup settings:', error);
      return {
        autoBackup: false,
        backupFrequency: 'weekly',
        maxBackups: 10
      };
    }
  }

  async updateBackupSettings(settings: Partial<AppSettings['backup']>): Promise<void> {
    try {
      console.log('⚙️ Updating backup settings in localStorage...');
      
      const currentSettings = await this.getBackupSettings();
      const updatedSettings = { ...currentSettings, ...settings };
      
      // Update localStorage immediately
      await universalStorage.setItem('nalc_backup_settings', updatedSettings);
      
      console.log('✅ Backup settings updated successfully');
    } catch (error) {
      console.error('Error updating backup settings:', error);
      throw error;
    }
  }

  private getDefaultSettings(): AppSettings {
    return {
      theme: 'system',
      language: 'en',
      timezone: 'UTC',
      dateFormat: 'MM/dd/yyyy',
      currency: 'USD',
      notifications: {
        enabled: true,
        sound: true,
        email: false,
        desktop: true
      },
      backup: {
        autoBackup: false,
        backupFrequency: 'weekly',
        maxBackups: 10
      },
      security: {
        sessionTimeout: 30,
        requirePassword: false
      },
      display: {
        itemsPerPage: 25,
        showImages: true,
        compactView: false
      }
    };
  }

  // Migration utility to preserve existing data
  async migrateExistingSettings(): Promise<void> {
    try {
      console.log('🔄 Migrating existing settings to localStorage-only with enhanced capacity...');
      
      // Migrate theme settings
      const theme = localStorage.getItem('theme');
      if (theme) {
        await this.setSetting('theme', theme);
      }

      // Migrate notification settings
      const notificationPermission = localStorage.getItem('notifications_permission');
      if (notificationPermission) {
        await this.updateNotificationSettings({
          enabled: notificationPermission === 'granted'
        });
      }

      // Migrate sound settings
      const soundSettings = localStorage.getItem('nalc_sound_settings');
      if (soundSettings) {
        const parsed = JSON.parse(soundSettings);
        await this.setSetting('sound_settings', parsed);
      }

      // Migrate password settings
      const passwordSettings = localStorage.getItem('nalc_password_settings');
      if (passwordSettings) {
        const parsed = JSON.parse(passwordSettings);
        await this.setSetting('password_settings', parsed);
      }

      console.log('✅ Settings migration completed successfully');
    } catch (error) {
      console.error('Error migrating settings:', error);
    }
  }

  // Clear cache
  clearCache(): void {
    this.settingsCache.clear();
  }

  // This always returns 'offline' since we're localStorage-only
  getSupabaseSyncStatus(): 'synced' | 'pending' | 'offline' {
    return 'offline';
  }
}

export const universalSettingsService = UniversalSettingsService.getInstance();
